import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import {
  Box,
  Button,
  Checkbox,
  Form,
  Icon,
  Input,
  Label,
  Link,
  PasswordStrengthIndicator,
} from "../../../common/components";
import { useForm } from "../../../common/hooks/useForm";
import { OnboardingContext } from "../../../context";
import { palette, spacing } from "../../../styles/theme";
import { UserOnboardingInfo, COUNTRY } from "../../../types";
import { testEmail, testPassword } from "../../../utils/FormValidationsUtils";
import { OnboardingTitle } from "../Component/OnboardingTitle";
import { Select } from "../../../common/components/Select/Select";

interface Props {
  onNextStep: (userInfo: UserOnboardingInfo) => void;
  onPreviousStep: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  userInfo: UserOnboardingInfo;
  setUserInfo: Dispatch<SetStateAction<UserOnboardingInfo>>;
  showPhoneField?: boolean;
  hideNewsletter?: boolean;
  submitLabel?: string;
}

const baseValidationSchema = object({
  email: string().test(testEmail),
  firstName: string().required(),
  lastName: string().required(),
  country: string().required().oneOf(Object.keys(COUNTRY)),
  password: string().required().test(testPassword),
});

const validationSchemaWithPhone = baseValidationSchema.shape({
  contactPhone: string().required(),
});

export const SignUp = (props: Props) => {
  const { t } = useTranslation();
  const {
    onNextStep,
    onPreviousStep,
    userInfo,
    showPhoneField,
    hideNewsletter,
    submitLabel,
  } = props;
  const { errorMsg } = useContext(OnboardingContext);

  const [showPasswordAsText, setShowPasswordAsText] = useState(false);

  const formik = useForm({
    initialValues: userInfo,
    onSubmit: (values) => {
      onNextStep(values);
    },
    validationSchema: showPhoneField
      ? validationSchemaWithPhone
      : baseValidationSchema,
  });

  const togglePasswordVisibility = useCallback(() => {
    setShowPasswordAsText((prev) => !prev);
  }, [setShowPasswordAsText]);

  return (
    <Box>
      <OnboardingTitle title={t("sign_in.create_account")} />
      <Form formik={formik}>
        <Box
          gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
          display="flex"
          flexDirection="column"
          justifyItems="center"
          fullWidth
        >
          <Input
            id="email"
            disabled
            fullWidth
            name="email"
            autoComplete="username"
            label={t("general.email")}
            data-test="signup.email"
            link={
              <Link onClick={onPreviousStep} smallFont>
                {t("sign_in.change_email")}
              </Link>
            }
          />
          <Box
            display="flex"
            gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
            flexDirection={{ xs: "column", lg: "row" }}
          >
            <Input
              type="text"
              label={t("sign_in.first_name")}
              autoComplete="given-name"
              id="given-name"
              name="firstName"
              data-test="signup.firstName"
              autoFocus
            />
            <Input
              type="text"
              label={t("sign_in.last_name")}
              autoComplete="family-name"
              id="family-name"
              name="lastName"
              data-test="signup.lastName"
            />
          </Box>

          <Select
            label={t("general.country.title")}
            name="country"
            data-test="signup.country"
            options={Object.keys(COUNTRY).map((key) => ({
              label: t(`general.country.${key}`),
              value: key,
            }))}
            value={formik.values.country ?? ""}
          />

          {showPhoneField && (
            <Input
              type="tel"
              label={t("onboard.contact_phone.label")}
              id="contactPhone"
              name="contactPhone"
              data-test="signup.contactPhone"
              value={formik.values.contactPhone ?? ""}
              fullWidth
            />
          )}

          <Box width="100%" maxWidth="400px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={spacing.smallDesktop}
              mb={0.5}
            >
              <Label htmlFor="new-password">{t("sign_in.password")}</Label>
              <PasswordStrengthIndicator password={formik.values.password} />
            </Box>
            <Input
              type={showPasswordAsText ? "text" : "password"}
              autoComplete="new-password"
              id="new-password"
              name="password"
              data-test="signup.password"
              fullWidth
              error={errorMsg || undefined}
              endAdornment={
                <Icon
                  style={{
                    cursor: "pointer",
                  }}
                  size={20}
                  icon={showPasswordAsText ? "visibility" : "visibility_off"}
                  onClick={togglePasswordVisibility}
                  color={palette.black.headings}
                />
              }
            />
          </Box>

          {!hideNewsletter && (
            <Checkbox
              onChange={(e) => {
                const optNewsletter = e.target.checked;
                formik.setFieldValue("optNewsletter", optNewsletter);
              }}
              sx={{ padding: 0 }}
              checked={formik.values.optNewsletter}
              id="optNewsletter"
              name="optNewsletter"
              label={t("sign_in.optNewsletter")}
              data-test="signup.optNewsletter"
            />
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={{ xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop }}
          mt={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
          fullWidth
        >
          <Button type="submit" fullWidth data-test="signup.submitButton">
            {submitLabel ?? t("sign_in.continue")}
          </Button>
          <Link onClick={(e) => onPreviousStep(e)}>{t("onboard.back")}</Link>
        </Box>
      </Form>
    </Box>
  );
};
